
import { useMemo } from 'react'

import { Button, Grid, Tooltip } from '@mui/material'
import { GridCellParams } from '@mui/x-data-grid'
import { format } from 'date-fns'

import { IGridColumn } from 'components/common'
import { DATE_TIME_FORMAT } from 'constants/DateFormats'

interface IGridColumnExtended extends IGridColumn {
  valueOptions?: any[]
  getActions?: (params: GridCellParams<any>) => React.ReactNode
}

const useGetDriverBidsColumns = (
  deallocateBidsLoading,
  allocateBidsLoading,
  handleAllocateClick,
  isRowSelectable,
  getGrossProfit,
  getGrossProfitPct,
  handleDisableBid,
) => {
  const columns: IGridColumnExtended[] = useMemo(
    () => [
      {
        field: 'primaryDriverName',
        headerName: 'Supplier Name',
        minWidth: 250,
        sortable: false,
        valueGetter: ({ row }) => row?.driver.primaryDriverName,
      },
      {
        field: 'price',
        headerName: 'Price',
        width: 100,
        sortPath: ['price'],
        renderCell: ({ row }) => row?.price ?? 'Unknown',
      },
      {
        field: 'currency',
        headerName: 'Currency',
        width: 70,
        sortPath: ['currency', 'id'],
        renderCell: ({ row }) => row?.currency.symbol ?? 'Unknown',
      },
      {
        field: 'grossProfit',
        headerName: 'Gross profit (£)',
        width: 110,
        type: 'number',
        sortable: false,
        valueGetter: ({ row }) => {
          return row?.price
            ? getGrossProfit({ totalPrice: row.price, currencyId: row.currency.id })
            : ''
        },
      },
      {
        field: 'grossProfitPct',
        headerName: 'Gross profit (%) (£)',
        width: 140,
        type: 'number',
        sortable: false,
        valueGetter: ({ row }) => {
          return row?.price
            ? getGrossProfitPct({ totalPrice: row.price, currencyId: row.currency.id })
            : ''
        },
      },
      {
        field: 'eta',
        headerName: 'ETA',
        width: 150,
        renderCell: ({ row }) => row?.eta && format(new Date(row?.eta), DATE_TIME_FORMAT),
      },
      {
        field: 'vehicle',
        headerName: 'Vehicle',
        width: 100,
        editable: false,
        sortable: true,
        sortPath: ['vehicle', 'vehicleType', 'name'],
        renderCell: ({ row }) => row?.vehicle.vehicleType.name ?? 'Unknown',
      },
      {
        field: 'rating',
        headerName: 'Rating',
        width: 100,
        sortPath: ['driver', 'averageRating'],
        renderCell: ({ row }) => row?.driver.averageRating ?? '',
      },
      {
        field: 'createDate',
        headerName: 'Date/time of creation',
        width: 150,
        sortPath: ['createdAt'],
        renderCell: ({ row }) =>
          row?.createdAt && format(new Date(row?.createdAt), DATE_TIME_FORMAT),
      },
      {
        field: 'notes',
        headerName: 'Notes',
        width: 150,
        renderCell: ({ row }) => row?.notes ?? 'No data',
      },
      {
        field: 'contactNumber ',
        headerName: 'Contact Number ',
        width: 150,
        sortable: false,
        renderCell: ({ row }) =>
          row?.driver?.telephoneNumber ?? row?.driver?.alternativeTelephoneNumber ?? 'N/A',
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 220,
        type: 'actions',
        renderCell: ({ id, row }) => {
          const disabled = allocateBidsLoading || deallocateBidsLoading || !row.isActive

          return (
            <Grid container spacing={1}>
              <Grid item>
                <Button disabled={disabled} variant='contained' onClick={handleAllocateClick(row)}>
                  {(row?.isAllocated ? isRowSelectable(id) : !isRowSelectable(id))
                    ? 'Deallocate'
                    : 'Allocate'}
                </Button>
              </Grid>

              <Grid item>
                <Tooltip title={'Dismiss'} followCursor>
                  <Button
                    disabled={disabled}
                    variant='outlined'
                    onClick={() => handleDisableBid(Number(row.id))}>
                    X
                  </Button>
                </Tooltip>
              </Grid>
            </Grid>
          )
        },
      },
    ],
    [
      allocateBidsLoading,
      deallocateBidsLoading,
      getGrossProfit,
      getGrossProfitPct,
      handleAllocateClick,
      isRowSelectable,
      handleDisableBid,
    ],
  )
  return columns
}

export default useGetDriverBidsColumns
